import React, { useRef } from "react";
import TasteProfileView from "./TasteProfileView";
import { adjacentScope, findScopeProfile } from "./profileScope";
import { ACCENTS } from "./TestSelector";

// PROFILE = "everything TASTE TEST currently knows about my taste," not just
// the last Test played. This is a thin navigation shell around the existing,
// untouched TasteProfileView: it picks which eligible Taste Signal card to
// show and lets the user move between them (swipe on mobile, tap a pill) —
// it never redesigns the card itself. Renders nothing extra when there's
// only one eligible scope, so a single-Test user sees exactly what they saw
// before this feature existed.

const SCOPE_TAB_LABEL = {
  FILM: "Film", SERIES: "Series", GAMES: "Game", SCREEN: "Screen", "CROSS-MEDIA": "Cross-Media",
};

const SWIPE_THRESHOLD_PX = 48;

// Same category colour language as the Home/Test-selector cards (TestSelector
// ACCENTS) — FILM/GAMES reuse the existing solid --tt-left/--tt-right tokens
// (already used at this same single-tone strength for the KEPT badge and the
// selector's confrontation glows); SERIES/SCREEN/CROSS-MEDIA reuse the exact
// existing perimeter gradients verbatim. Only the ACTIVE pill gets a fill.
const ACTIVE_PILL_BG = {
  FILM: "var(--tt-left)",
  SERIES: ACCENTS.SERIES.perimeter,
  GAMES: "var(--tt-right)",
  SCREEN: "linear-gradient(90deg, var(--tt-left), var(--tt-right))",
  "CROSS-MEDIA": ACCENTS["CROSS-MEDIA"].perimeter,
};

function ScopeTabs({ scopes, selected, onSelect }) {
  return (
    <div
      data-testid="profile-scope-tabs"
      className="flex items-center gap-1.5 overflow-x-auto no-scrollbar"
    >
      {scopes.map((s) => {
        const active = s.scope === selected;
        return (
          <button
            key={s.scope}
            type="button"
            data-testid={`profile-scope-tab-${s.scope}`}
            aria-current={active}
            onClick={() => onSelect(s.scope)}
            className={`shrink-0 rounded-full px-3 py-1.5 font-mono text-[10px] font-bold tracking-[0.16em] uppercase transition-colors ${
              active ? "text-[#05070a]" : "text-subtle hover:text-white/80 bg-white/[0.06] border border-white/12"
            }`}
            style={active ? { background: ACTIVE_PILL_BG[s.scope] } : undefined}
          >
            {SCOPE_TAB_LABEL[s.scope] || s.scopeLabel}
          </button>
        );
      })}
    </div>
  );
}

export default function ProfileCarousel({ scopes, selectedScope, onSelectScope, ...profileViewProps }) {
  const touchStart = useRef(null);

  if (!scopes || scopes.length === 0) return null;
  const profile = findScopeProfile(scopes, selectedScope) || scopes[0];
  const multi = scopes.length > 1;

  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e) => {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) > SWIPE_THRESHOLD_PX && Math.abs(dx) > Math.abs(dy) * 1.5) {
      onSelectScope(adjacentScope(scopes, profile.scope, dx < 0 ? 1 : -1));
    }
  };

  return (
    <div onTouchStart={multi ? onTouchStart : undefined} onTouchEnd={multi ? onTouchEnd : undefined}>
      <TasteProfileView
        profile={profile}
        scopeNav={multi ? <ScopeTabs scopes={scopes} selected={profile.scope} onSelect={onSelectScope} /> : null}
        {...profileViewProps}
      />
    </div>
  );
}
